/**
 * Reminder display formatters (#198).
 *
 * Pure helpers — no React, no fetch — so they're trivial to unit test.
 * Used by Portal's RemindersSection to render the "Pending" / "Failed"
 * / "Completed" lists.
 */

const DOW_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * One-line human description of when a reminder fires.
 *
 * Handles every recurrence shape the API currently emits:
 *   - normal w/ due_date only          → "Tuesday May 5"
 *   - important once + due+time        → "Tuesday May 5 at 15:00"
 *   - important daily                  → "Every day at 09:00"
 *   - important weekly + recur_days    → "Every Mon, Wed, Fri at 09:00"
 *   - important interval N days        → "Every 3 days at 14:00"
 *   - yearly (birthday) + recur_date   → "Annually on May 29"
 *   - legacy birthday (recurring=true) → "Annually on May 3"
 *   - fallback                         → "Schedule: not set"
 *
 * Notes:
 *   - recur_days uses Python weekday() indexing: 0=Mon … 6=Sun. Mirror
 *     here so the display matches what the scheduler will actually do.
 *   - scheduled_time arrives as "HH:MM:SS" — we slice to HH:MM.
 */
export function formatReminderSchedule(r) {
  if (!r) return '';
  const time = r.scheduled_time ? String(r.scheduled_time).slice(0, 5) : null;

  // Legacy birthday-style: recurring=true with recur_date "MM-DD"
  if (r.recurring && r.recur_date) {
    const parts = String(r.recur_date).split('-');
    if (parts.length >= 2) {
      const mm = parseInt(parts[parts.length - 2], 10);
      const dd = parseInt(parts[parts.length - 1], 10);
      if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
        return `Annually on ${MONTHS_LONG[mm - 1]} ${dd}`;
      }
    }
    return `Annually on ${r.recur_date}`;
  }

  // New recurrence model (important reminders).
  // 'yearly' is the new birthday-anniversary value introduced with the
  // in-conversation reminder-offer flow. recur_date is the MM-DD; same
  // shape as the legacy `recurring` branch above but uses the new
  // recurrence-string convention so server-side filters (scheduler
  // _reminder_due_today, voice.py conversation-mode loader) work. Pre-fix
  // the row rendered "Schedule: not set" because the case didn't exist
  // here yet — confirmed on staging 2026-05-14 after Berta auto-created
  // Maria's birthday reminder for May 29.
  if (r.recurrence === 'yearly' && r.recur_date) {
    const parts = String(r.recur_date).split('-');
    if (parts.length >= 2) {
      const mm = parseInt(parts[parts.length - 2], 10);
      const dd = parseInt(parts[parts.length - 1], 10);
      if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
        return `Annually on ${MONTHS_LONG[mm - 1]} ${dd}${time ? ` at ${time}` : ''}`;
      }
    }
    return `Annually on ${r.recur_date}${time ? ` at ${time}` : ''}`;
  }

  if (r.recurrence === 'daily' && time) {
    return `Every day at ${time}`;
  }
  if (r.recurrence === 'weekly' && Array.isArray(r.recur_days) && r.recur_days.length > 0) {
    const days = r.recur_days
      .filter(d => Number.isInteger(d) && d >= 0 && d <= 6)
      .slice()
      .sort((a, b) => a - b)
      .map(d => DOW_SHORT[d])
      .join(', ');
    return `Every ${days}${time ? ` at ${time}` : ''}`;
  }
  if (r.recurrence === 'interval' && r.recur_interval_days) {
    const n = r.recur_interval_days;
    return `Every ${n === 1 ? 'day' : `${n} days`}${time ? ` at ${time}` : ''}`;
  }

  // Once-off — covers normal reminders with due_date and important
  // recurrence='once' + due_date + scheduled_time.
  if (r.due_date) {
    // Treat YYYY-MM-DD as a calendar date in local time. Splitting
    // manually avoids the UTC-shift you get from `new Date("2026-05-05")`
    // in some timezones (which is the kind of bug we don't want anyone
    // to ever see in a reminder).
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(r.due_date));
    if (m) {
      const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
      if (!isNaN(d.getTime())) {
        const formatted = d.toLocaleDateString(undefined, {
          weekday: 'long', month: 'long', day: 'numeric',
        });
        return time ? `${formatted} at ${time}` : formatted;
      }
    }
    return time ? `${r.due_date} at ${time}` : String(r.due_date);
  }

  if (time) return `At ${time}`;
  return 'Schedule: not set';
}

/**
 * Compact secondary line — source, created date, delivery history —
 * shown beneath the schedule. Returns an array of strings the caller
 * joins with " · " or similar.
 */
export function formatReminderMeta(r) {
  if (!r) return [];
  const out = [];

  if (r.source === 'family') out.push('Set by family');
  else if (r.source === 'portal') out.push('From the portal');
  else if (r.source === 'intake') out.push('From intake');
  else if (r.source === 'user') out.push('From a call');
  else if (r.source) out.push(`Source: ${r.source}`);

  if (r.created_at) {
    const d = new Date(r.created_at);
    if (!isNaN(d.getTime())) {
      out.push(`Added ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`);
    }
  }

  if (typeof r.delivery_count === 'number' && r.delivery_count > 0) {
    out.push(`Delivered ${r.delivery_count}×`);
  }

  if (r.last_delivered_at) {
    const d = new Date(r.last_delivered_at);
    if (!isNaN(d.getTime())) {
      out.push(`Last ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`);
    }
  }

  if (r.retry_count && r.retry_count > 0) {
    out.push(`${r.retry_count}/3 attempts`);
  }

  return out;
}
